import React, { useState } from "react";
import {
  FaWalking,
  FaBus,
  FaSubway,
  FaTrain,
  FaCar,
  FaBicycle,
  FaMotorcycle,
  FaChevronDown,
  FaChevronUp,
  FaMapMarkerAlt,
  FaArrowRight,
} from "react-icons/fa";
import { MdTram, MdDirectionsBoat } from "react-icons/md";

// Get icon for transit vehicle type
const getTransitIcon = (vehicleType) => {
  switch (vehicleType?.toLowerCase()) {
    case "bus":
      return <FaBus />;
    case "subway":
    case "metro":
      return <FaSubway />;
    case "rail":
    case "train":
    case "heavy_rail":
    case "commuter_train":
      return <FaTrain />;
    case "tram":
    case "light_rail":
      return <MdTram />;
    case "ferry":
      return <MdDirectionsBoat />;
    default:
      return <FaBus />;
  }
};

// Get icon for travel mode
const getModeIcon = (mode) => {
  switch (mode) {
    case "DRIVING":
      return <FaCar />;
    case "WALKING":
      return <FaWalking />;
    case "BICYCLING":
      return <FaBicycle />;
    case "TWO_WHEELER":
      return <FaMotorcycle />;
    default:
      return <FaWalking />;
  }
};

// Transit step component
const TransitStep = ({ step, index }) => {
  const [expanded, setExpanded] = useState(false);
  const transit = step.transit;

  return (
    <div style={stepCardStyle}>
      <div
        style={stepHeaderStyle}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={stepIconContainerStyle}>
          <div
            style={{
              ...transitBadgeStyle,
              backgroundColor: transit?.line?.color || "#3f51b5",
              color: transit?.line?.text_color || "#fff",
            }}
          >
            {getTransitIcon(transit?.line?.vehicle?.type)}
          </div>
        </div>

        <div style={stepContentStyle}>
          <div style={stepTitleStyle}>
            <span style={{ fontWeight: 600 }}>
              {transit?.line?.short_name || transit?.line?.name || "Transit"}
            </span>
            <span style={stepDurationStyle}>{step.duration?.text}</span>
          </div>
          <div style={stepSubtitleStyle}>
            {transit?.departure_stop?.name} → {transit?.arrival_stop?.name}
          </div>
          <div style={stepMetaStyle}>
            {transit?.num_stops} stops • {step.distance?.text}
          </div>
        </div>

        <div style={expandIconStyle}>
          {expanded ? <FaChevronUp /> : <FaChevronDown />}
        </div>
      </div>

      {expanded && (
        <div style={expandedContentStyle}>
          <div style={stopDetailStyle}>
            <FaMapMarkerAlt style={{ color: "#4caf50", marginRight: 8 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                Depart: {transit?.departure_stop?.name}
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>
                {transit?.departure_time?.text}
              </div>
            </div>
          </div>

          <div style={{ borderLeft: "2px dashed #ddd", marginLeft: 7, paddingLeft: 20, paddingTop: 8, paddingBottom: 8 }}>
            <div style={{ fontSize: 12, color: "#666" }}>
              Ride {transit?.num_stops} stops ({step.duration?.text})
            </div>
            {transit?.headsign && (
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                Direction: {transit.headsign}
              </div>
            )}
          </div>

          <div style={stopDetailStyle}>
            <FaMapMarkerAlt style={{ color: "#f44336", marginRight: 8 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                Arrive: {transit?.arrival_stop?.name}
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>
                {transit?.arrival_time?.text}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Walking step component
const WalkingStep = ({ step, isFirst, isLast }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={walkingStepStyle}>
      <div
        style={walkingHeaderStyle}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={walkingIconStyle}>
          <FaWalking />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>
            {isFirst ? "Walk to station" : isLast ? "Walk to destination" : "Walk to next stop"}
          </div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {step.duration?.text} • {step.distance?.text}
          </div>
        </div>
        {step.steps?.length > 0 && (
          <div style={expandIconStyle}>
            {expanded ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        )}
      </div>

      {expanded && step.steps?.length > 0 && (
        <div style={walkingDetailsStyle}>
          {step.steps.map((subStep, i) => (
            <div key={i} style={walkingSubStepStyle}>
              <FaArrowRight style={{ color: "#999", marginRight: 8, fontSize: 10 }} />
              <div
                style={{ fontSize: 12, color: "#555" }}
                dangerouslySetInnerHTML={{ __html: subStep.instructions }}
              />
              <span style={{ fontSize: 11, color: "#999", marginLeft: 8 }}>
                {subStep.distance?.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Driving/Cycling step component
const DrivingStep = ({ step, index }) => {
  return (
    <div style={drivingStepStyle}>
      <div style={stepNumberStyle}>{index + 1}</div>
      <div style={{ flex: 1 }}>
        <div
          style={{ fontSize: 13, color: "#333" }}
          dangerouslySetInnerHTML={{ __html: step.instructions }}
        />
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
          {step.distance?.text} • {step.duration?.text}
        </div>
      </div>
    </div>
  );
};

// Main RouteSteps component
const RouteSteps = ({ route, travelMode, onClose }) => {
  if (!route || !route.legs || !route.legs[0]) return null;

  const leg = route.legs[0];
  const steps = leg.steps || [];

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {getModeIcon(travelMode)}
          <span style={{ fontWeight: 600 }}>Step-by-step directions</span>
        </div>
        <button onClick={onClose} style={closeButtonStyle}>×</button>
      </div>

      {/* Summary */}
      <div style={summaryStyle}>
        <div style={{ fontSize: 18, fontWeight: "bold" }}>
          {leg.duration?.text}
        </div>
        <div style={{ fontSize: 13, color: "#666" }}>
          {leg.distance?.text} • via {route.summary || "recommended route"}
        </div>
      </div>

      {/* Steps */}
      <div style={stepsContainerStyle}>
        {/* Start location */}
        <div style={locationMarkerStyle}>
          <div style={{ ...markerDotStyle, backgroundColor: "#4caf50" }} />
          <div style={locationTextStyle}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Start</div>
            <div style={{ fontSize: 12, color: "#666" }}>{leg.start_address}</div>
          </div>
        </div>

        {/* Route steps */}
        {travelMode === "TRANSIT" ? (
          // Transit mode - show transit and walking steps
          steps.map((step, index) => {
            if (step.travel_mode === "TRANSIT") {
              return <TransitStep key={index} step={step} index={index} />;
            } else {
              return (
                <WalkingStep
                  key={index}
                  step={step}
                  isFirst={index === 0}
                  isLast={index === steps.length - 1}
                />
              );
            }
          })
        ) : (
          // Driving/Walking/Cycling - show turn-by-turn
          steps.map((step, index) => (
            <DrivingStep key={index} step={step} index={index} />
          ))
        )}

        {/* End location */}
        <div style={locationMarkerStyle}>
          <div style={{ ...markerDotStyle, backgroundColor: "#f44336" }} />
          <div style={locationTextStyle}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Destination</div>
            <div style={{ fontSize: 12, color: "#666" }}>{leg.end_address}</div>
          </div>
        </div>
      </div>

      {/* Departure info for transit */}
      {travelMode === "TRANSIT" && leg.departure_time && (
        <div style={departureInfoStyle}>
          <div style={{ fontSize: 12, color: "#666" }}>
            Depart at <strong>{leg.departure_time.text}</strong>, arrive by{" "}
            <strong>{leg.arrival_time?.text}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle = {
  background: "#fff",
  borderRadius: 12,
  marginTop: 12,
  border: "1px solid #eee",
  overflow: "hidden",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 16px",
  background: "#f8f9fa",
  borderBottom: "1px solid #eee",
  fontSize: 14,
  color: "#333",
};

const closeButtonStyle = {
  background: "none",
  border: "none",
  fontSize: 20,
  color: "#666",
  cursor: "pointer",
  padding: 0,
  lineHeight: 1,
};

const summaryStyle = {
  padding: "12px 16px",
  borderBottom: "1px solid #eee",
};

const stepsContainerStyle = {
  padding: "12px 16px",
  maxHeight: 300,
  overflowY: "auto",
};

const locationMarkerStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "8px 0",
};

const markerDotStyle = {
  width: 14,
  height: 14,
  borderRadius: "50%",
  marginTop: 2,
  flexShrink: 0,
};

const locationTextStyle = {
  flex: 1,
};

const stepCardStyle = {
  background: "#f8f9fa",
  borderRadius: 10,
  marginBottom: 8,
  overflow: "hidden",
  border: "1px solid #eee",
};

const stepHeaderStyle = {
  display: "flex",
  alignItems: "center",
  padding: 12,
  cursor: "pointer",
  gap: 12,
};

const stepIconContainerStyle = {
  flexShrink: 0,
};

const transitBadgeStyle = {
  width: 36,
  height: 36,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 16,
};

const stepContentStyle = {
  flex: 1,
  minWidth: 0,
};

const stepTitleStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: 14,
};

const stepDurationStyle = {
  fontSize: 12,
  color: "#666",
  fontWeight: 500,
};

const stepSubtitleStyle = {
  fontSize: 12,
  color: "#555",
  marginTop: 2,
};

const stepMetaStyle = {
  fontSize: 11,
  color: "#888",
  marginTop: 2,
};

const expandIconStyle = {
  color: "#999",
  fontSize: 12,
};

const expandedContentStyle = {
  padding: "0 12px 12px",
  background: "#fff",
};

const stopDetailStyle = {
  display: "flex",
  alignItems: "flex-start",
  padding: "8px 0",
};

const walkingStepStyle = {
  padding: "8px 0",
  borderLeft: "2px dashed #ccc",
  marginLeft: 6,
  paddingLeft: 18,
};

const walkingHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
};

const walkingIconStyle = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  background: "#e3f2fd",
  color: "#1976d2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
};

const walkingDetailsStyle = {
  marginTop: 8,
  marginLeft: 38,
  padding: "8px 12px",
  background: "#fafafa",
  borderRadius: 8,
};

const walkingSubStepStyle = {
  display: "flex",
  alignItems: "flex-start",
  padding: "4px 0",
};

const drivingStepStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "10px 0",
  borderBottom: "1px solid #f0f0f0",
};

const stepNumberStyle = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  background: "#3f51b5",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 600,
  flexShrink: 0,
};

const departureInfoStyle = {
  padding: "12px 16px",
  background: "#e8f5e9",
  borderTop: "1px solid #c8e6c9",
};

export default RouteSteps;
